import ExcelJs from 'exceljs'
import moment from 'moment'
import { Mark, Slot, Student, Subject } from '@/plugins/api'
import { get, uniq } from 'lodash'

function getEvaluateValue(val = '') {
  switch (val) {
    case 1:
      return 'Đ'
    case 2:
      return 'KĐ'
    default:
      return ''
  }
}

function getContentDataRow({ student = {}, marks = [], subjects = [], index = 0 }) {
  const row = [String(index + 1), student.code, student.name, moment(student.dob).format('DD/MM/YYYY')]

  subjects.forEach(subject => {
    const mark = marks.find(x => x.subject === subject.id)
    if (!mark) row.push('')
    else if (subject.markType === 'evaluate') row.push(getEvaluateValue(mark.value))
    else row.push(mark.value)
  })

  return row
}

function setHeader(worksheet) {
  worksheet.getRow(5).eachCell(cell => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.font = { bold: true }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }

    let selectedHeaders = ['STT', 'Mã định danh', 'Họ và tên', 'Ngày sinh']
    if (selectedHeaders.includes(cell.value)) {
      cell.font = { bold: true, color: { argb: 'FFFF0000' } }
    } else {
      cell.font = { bold: true }
    }
  })
}

function settingCells(worksheet) {
  worksheet.getColumn(1).eachCell((cell, rowNumber) => {
    if (rowNumber >= 2) {
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
    }
  })
  worksheet.getRow(5).height = 60

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 4) {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      })
    }
  })
}

function getTitle({ ws = {}, semester = {}, classData = {}, factorTitle = '', currentSchoolYear = {} }) {
  const description11 = 'Sở Giáo Dục và Đào Tạo Hà Nội'
  const description12 = 'Trường: THPT Chuyên Đại Học Sư Phạm'

  const description21 = `BẢNG ĐIỂM ${factorTitle.toUpperCase()} LỚP ${classData.title.toUpperCase()} THEO MÔN HỌC`
  const description22 = `${semester.title} ${currentSchoolYear.name}`.toUpperCase()

  ws.addRow([description11, '', '', description21, '', ''])
  ws.mergeCells(1, 1, 1, 3)
  ws.mergeCells(1, 4, 1, 7)

  ws.addRow([description12, '', '', description22, '', ''])
  ws.mergeCells(2, 1, 2, 3)
  ws.mergeCells(2, 4, 2, 7)

  ws.addRow('')

  ws.getCell('A1').alignment = { horizontal: 'left' }
  ws.getCell('D1').alignment = { horizontal: 'center' }

  ws.getCell('A2').alignment = { horizontal: 'left' }
  ws.getCell('D2').alignment = { horizontal: 'center' }

  ws.getCell('A3').alignment = { horizontal: 'left' }
  ws.getCell('D3').alignment = { horizontal: 'center' }
  ws.addRow([])
}

async function onDownloadFile({ wb = {}, classData = {}, semester = {}, factorTitle = '' }) {
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'test.xlsx'
  link.download = `Bảng điểm ${factorTitle} Lớp ${classData.title} HK ${semester.title}.xlsx`
  link.click()
}

const generateFile = async ({
  semester = {},
  classData = {},
  factorTitle = '',
  currentSchoolYear = {},
  subjects = [],
  studentMarks = []
}) => {
  const wb = new ExcelJs.Workbook()
  const ws = wb.addWorksheet('diem-theo-mon')

  getTitle({ ws, semester, currentSchoolYear, classData, factorTitle })

  const header = ['STT', 'Mã định danh', 'Họ và tên', 'Ngày sinh']

  const marksHeader = []
  const contentData = []

  subjects.forEach(subject => {
    marksHeader.push(get(subject, 'subjectGroup.title', ''))
  })
  studentMarks.forEach((item, index) => {
    const { student, marks } = item
    contentData.push(getContentDataRow({ marks, subjects, student, index }))
  })

  ws.addRow(header.concat(marksHeader))
  contentData.forEach(row => {
    ws.addRow(row)
  })

  setHeader(ws)
  settingCells(ws)

  ws.columns = [{ width: 10 }, { width: 15 }, { width: 30 }, { width: 15 }, ...marksHeader.map(() => ({ width: 10 }))]

  await onDownloadFile({ wb, classData, semester, factorTitle })
}

const getSubjects = async (slots = [], titleNePostfix = '') => {
  const subjectIds = uniq(slots.map(x => x.subject.id))
  const subjects = await Subject.fetch({
    id_in: subjectIds,
    _populate: 'subjectGroup',
    _sort: 'subjectGroupIndex:ASC'
  })

  return subjects
}

const orderStudentAlphabetic = (students = []) => {
  const collator = new Intl.Collator('vi')
  return students
    .map(s => {
      const fn = s.formatedName.split(' ').shift()
      const ln = s.formatedName.replace(`${fn} `, '')
      return { ...s, fn, ln }
    })
    .sort((a, b) => {
      const r = collator.compare(a.fn, b.fn)
      return r !== 0 ? r : collator.compare(a.ln, b.ln)
    })
}

const exec = async ({ currentSchoolYear = {}, semesterData = {}, classData = {}, factorData = {} }) => {
  let [slots, rawMarks, students] = await Promise.all([
    Slot.fetch({ grade: classData.grade.id, _populate: 'subject' }),
    Mark.fetch({
      semester: semesterData.id,
      class: classData.id,
      _limit: -1,
      _populate: 'factor'
    }),
    Student.fetch({ currentClass: classData.id, _populate: 'academicLevel' })
  ])
  rawMarks = rawMarks.filter(m => m.factor.type === factorData.value)
  const subjects = await getSubjects(slots, classData.grade.gradeNumber)
  students = orderStudentAlphabetic(students)
  const studentMarks = students.map(student => ({
    student,
    marks: rawMarks.filter(m => m.student === student.id)
  }))

  await generateFile({
    semester: semesterData,
    classData,
    currentSchoolYear,
    studentMarks,
    subjects,
    factorTitle: factorData.title
  })
}

export default { exec }
